/**
 * Engine Registry — maps route param to the correct sauna engine.
 */

import * as THREE from "three";
import { calculateDeck } from "../domain/deck/calculateDeck";
import { DEFAULT_ROOF_CONFIG_BY_TYPE } from "../domain/roof/roofConfig";
import { barrelSaunaEngine } from "./barrelSauna";
import { ENGINE_JOIST_H_M, ENGINE_PANEL_T_M } from "./constants";
import { buildDeckGeometry } from "./deckGeometry";
import { SQUARE_DEFAULT_DIMENSIONS } from "./defaultDimensions";
import { DirtyFlag } from "./dirtyTracking";
import { enrichCalculationResult } from "./enrichmentPipeline";
import { modifyIndoorBuildGuideSteps } from "./indoorBuildGuide";
import {
	resolveInteriorBom,
	resolveInteriorRenderPlan,
} from "./interiorLayoutResolver";
import { addInteriorFixtures } from "./interiorFixtures";
import { applyOpeningAdjustments } from "./openingAdjustments";
import {
	getSquareAuditMetadata,
	getSquareEvidenceConfig,
	getTwoRoomAuditMetadata,
	getTwoRoomEvidenceConfig,
} from "./rectangularAudit";
import {
	getStructuralRoofJoists,
	resolveAdvisoryRoofJoists,
} from "./roofJoistSizing";
import {
	buildSauna3D as buildSquare,
	buildSquareSubsystem,
	calculateMaterials as calcSquare,
	getDefaultCameraPosition as camSquare,
	createSquareBuildContext,
	getSquareCameraPresets,
} from "./squareSauna";
import { resolveTwoRoomConfig, twoRoomSaunaEngine } from "./twoRoomSauna";
import type { BuildInput, SaunaEngine } from "./types";

function getDeckFoundationHeight(): number {
	// The deck board-top Y must match the sauna floor surface Y.
	// In the 3D coordinate system Y=0 is beam-top; the floor surface
	// sits at fH (joist height) + pT (panel thickness).
	return ENGINE_JOIST_H_M + ENGINE_PANEL_T_M;
}

function withInteriorAndDeckGeometry<
	T extends ReturnType<SaunaEngine["buildSauna3D"]>,
>(result: T, input: BuildInput): T {
	addInteriorFixtures(result.interiorGroup, input);
	if (!input.deckConfig) {
		return result;
	}

	const foundationHeight = getDeckFoundationHeight();

	result.deckGroup.clear();
	result.deckGroup.add(
		buildDeckGeometry(
			input.deckConfig,
			input.dimensions,
			foundationHeight,
			calculateDeck(input.deckConfig, input.dimensions),
		),
	);

	if (!result.saunaGroup.children.includes(result.deckGroup)) {
		result.saunaGroup.add(result.deckGroup);
	}

	return result;
}

function addInteriorFixturesIfNeeded(
	flag: DirtyFlag,
	input: BuildInput,
	group: THREE.Group,
): THREE.Group {
	return flag === DirtyFlag.Interior
		? addInteriorFixtures(group, input)
		: group;
}

function buildDeckSubsystem(input: BuildInput): THREE.Group {
	const group = new THREE.Group();
	if (!input.deckConfig) {
		return group;
	}

	const foundationHeight = getDeckFoundationHeight();

	group.add(
		buildDeckGeometry(
			input.deckConfig,
			input.dimensions,
			foundationHeight,
			calculateDeck(input.deckConfig, input.dimensions),
		),
	);
	return group;
}

// Wrap the existing square engine in the SaunaEngine interface
const squareSaunaEngine: SaunaEngine = {
	label: "Fyrkantig Bastu",
	icon: "🏠",
	description: "Beräknar stomme enligt c/c 600 & BBR",
	defaultDimensions: SQUARE_DEFAULT_DIMENSIONS,
	buildSauna3D: (input) => {
		const roofJoists = resolveAdvisoryRoofJoists(input);
		return withInteriorAndDeckGeometry(
			buildSquare(
				input.dimensions.width,
				input.dimensions.length,
				input.openings,
				input.roofConfig ?? undefined,
				input.siteConditions,
				input.deckConfig,
				input.thermalInputs,
				getStructuralRoofJoists(roofJoists),
				input.thermalInputs.heaterPlacementZMm,
				{
					// Same resolver the BOM bills from — see `resolveInteriorRenderPlan`.
					interior: resolveInteriorRenderPlan(input, "square") ?? undefined,
					finishes: input.finishes,
					wallHeightM: input.wallHeightM,
					siting: input.siting,
					indoorRoom: input.indoorRoom,
					chimneyInput: input,
				},
			),
			input,
		);
	},
	calculateMaterials: (input) => {
		const roofJoists = resolveAdvisoryRoofJoists(input);
		// The interior layout is resolved ONCE per calculation, here, and the
		// derived bench rows are handed down. The engine modules cannot reach the
		// resolver themselves without closing an import cycle.
		const interior = resolveInteriorBom(input);
		let r = applyOpeningAdjustments(
			calcSquare(
				input.dimensions.width,
				input.dimensions.length,
				input.roofConfig ?? undefined,
				input.siteConditions,
				input.thermalInputs.heaterType,
				getStructuralRoofJoists(roofJoists),
				interior?.bench,
				{
					finishes: input.finishes,
					wallHeightM: input.wallHeightM,
					siting: input.siting,
					indoorRoom: input.indoorRoom,
				},
			),
			input,
		);
		r = { ...r, roofJoists, interiorLayout: interior?.layout };
		return enrichCalculationResult(r, input, interior?.context);
	},
	getAuditMetadata: () => getSquareAuditMetadata(),
	getEvidenceConfig: (input, calculation) =>
		getSquareEvidenceConfig(input, calculation),
	getDefaultCameraPosition: camSquare,
	getCameraPresets: (input) =>
		getSquareCameraPresets(
			input.dimensions.width,
			input.dimensions.length,
			resolveInteriorRenderPlan(input, "square")?.benchFocusM,
		),
	createBuildContext: (input) =>
		createSquareBuildContext(
			input.dimensions.width,
			input.dimensions.length,
			input.roofConfig ?? undefined,
			getStructuralRoofJoists(resolveAdvisoryRoofJoists(input)),
			input.wallHeightM,
		),
	buildSubsystem: (subsystem, input, ctx) =>
		subsystem === DirtyFlag.Deck
			? buildDeckSubsystem(input)
			: addInteriorFixturesIfNeeded(
					subsystem,
					input,
					buildSquareSubsystem(
						subsystem,
						input,
						ctx as ReturnType<typeof createSquareBuildContext>,
						// The interior stands the stove on it and the roof trims its
						// joists around that stove's flue; resolving it here keeps the
						// "engines never resolve their own layout" rule intact for every
						// path into the builders.
						subsystem === DirtyFlag.Interior || subsystem === DirtyFlag.Roof
							? (resolveInteriorRenderPlan(input, "square") ?? undefined)
							: undefined,
					),
				),
};

/**
 * The two-room engine sniffs four optional fields to decide it IS a two-room
 * (see `calculateMaterials` below). The interior resolver does the same, so it
 * has to be handed the materialised config or it measures a fyrkantig — a
 * different room and a different heater slot.
 */
function twoRoomInteriorPlan(input: BuildInput) {
	return (
		resolveInteriorRenderPlan(
			{ ...input, ...resolveTwoRoomConfig(input) },
			"two-room",
		) ?? undefined
	);
}

const twoRoomWithOpenings: SaunaEngine = {
	...twoRoomSaunaEngine,
	buildSauna3D: (input) =>
		withInteriorAndDeckGeometry(
			twoRoomSaunaEngine.buildSauna3D(input, twoRoomInteriorPlan(input)),
			{ ...input, ...resolveTwoRoomConfig(input) },
		),
	getCameraPresets: (input) =>
		twoRoomSaunaEngine.getCameraPresets?.(
			input,
			twoRoomInteriorPlan(input)?.benchFocusM,
		) ?? [],
	calculateMaterials: (input) => {
		// Materialise the two-room config onto the input before anything reads
		// it. Several helpers below the registry decide "is this a two-room?" by
		// sniffing for these four fields (`deriveHeaterClearanceLayout`,
		// `deriveInteriorLayoutContext`), so an input that leaves them all to
		// their defaults was being measured as a plain fyrkantig — a different
		// room, a different heater position. `resolveTwoRoomConfig` returns the
		// same values the engine already applies, so this changes nothing for an
		// input that spelled them out.
		const config = resolveTwoRoomConfig(input);
		const twoRoomInput = { ...input, ...config };
		// "(Bastudel)" marks the hot room's share on a design that also has a
		// relax room — the same suffix the other two-room interior rows carry.
		const interior = resolveInteriorBom(
			twoRoomInput,
			config.hasRelaxRoom ? " (Bastudel)" : "",
		);
		const raw = applyOpeningAdjustments(
			twoRoomSaunaEngine.calculateMaterials(twoRoomInput, interior?.bench),
			twoRoomInput,
		);
		return enrichCalculationResult(
			{ ...raw, interiorLayout: interior?.layout },
			twoRoomInput,
			interior?.context,
		);
	},
	getAuditMetadata: () => getTwoRoomAuditMetadata(),
	getEvidenceConfig: (input, calculation) =>
		getTwoRoomEvidenceConfig(input, calculation),
	buildSubsystem: (subsystem, input, ctx) =>
		subsystem === DirtyFlag.Deck
			? buildDeckSubsystem(input)
			: addInteriorFixturesIfNeeded(
					subsystem,
					{ ...input, ...resolveTwoRoomConfig(input) },
					twoRoomSaunaEngine.buildSubsystem?.(
						subsystem,
						input,
						ctx,
						twoRoomInteriorPlan(input),
					) ?? new THREE.Group(),
				),
};

const barrelWithElectrical: SaunaEngine = {
	...barrelSaunaEngine,
	buildSauna3D: (input) =>
		withInteriorAndDeckGeometry(barrelSaunaEngine.buildSauna3D(input), input),
	calculateMaterials: (input) =>
		enrichCalculationResult(
			barrelSaunaEngine.calculateMaterials(input),
			input,
			undefined,
			"barrel",
		),
};

/**
 * Issue 156 — a sauna built as a room inside an existing building.
 *
 * The square engine with `siting: "indoor"` stamped on its input: the same
 * walls, insulation, bastufolie, panel, lavar and aggregat, but no
 * foundation, facade or roof covering, and a flat ceiling. The enrichment
 * chain runs unchanged and reads `siting` itself (permit, snow load,
 * drainage, placement), so nothing indoor-specific is duplicated here.
 *
 * The roof and deck are not choices indoors, so they are pinned rather than
 * trusted from the draft — a design switched from fyrkantig keeps its old
 * roof and deck in the store.
 */
export function asIndoorInput(input: BuildInput): BuildInput {
	return {
		...input,
		siting: "indoor",
		roofConfig: DEFAULT_ROOF_CONFIG_BY_TYPE.inomhus,
		deckConfig: null,
	};
}

const indoorSaunaEngine: SaunaEngine = {
	...squareSaunaEngine,
	label: "Inomhusbastu",
	modifyBuildGuideSteps: modifyIndoorBuildGuideSteps,
	description: "Bastu i befintligt rum – utan grund, tak och fasad",
	buildSauna3D: (input) => squareSaunaEngine.buildSauna3D(asIndoorInput(input)),
	calculateMaterials: (input) =>
		squareSaunaEngine.calculateMaterials(asIndoorInput(input)),
	getEvidenceConfig: (input, calculation) =>
		squareSaunaEngine.getEvidenceConfig(asIndoorInput(input), calculation),
	getCameraPresets: (input) =>
		squareSaunaEngine.getCameraPresets?.(asIndoorInput(input)) ?? [],
	createBuildContext: (input) =>
		squareSaunaEngine.createBuildContext?.(asIndoorInput(input)),
	buildSubsystem: (subsystem, input, ctx) =>
		squareSaunaEngine.buildSubsystem?.(subsystem, asIndoorInput(input), ctx) ??
		new THREE.Group(),
};

const ENGINES: Record<string, SaunaEngine> = {
	fyrkantig: squareSaunaEngine,
	inomhus: indoorSaunaEngine,
	tunna: barrelWithElectrical,
	tvarum: twoRoomWithOpenings,
};

export function getEngine(type: string): SaunaEngine {
	return ENGINES[type] ?? squareSaunaEngine;
}
